/**
 * Snapshot of the INITIAL `location.search` (deep link).
 *
 * Problem: when loading the app through a deep URL with search params
 * (`/purchases?po=…`, `/stock?product=…`, `/lots?product=…`), TanStack Router
 * makes its first navigation "commit" deferred (microtask `flush` from
 * `@tanstack/history`) and, during that boot, the URL can be rewritten
 * losing search params before the target route gets to read them.
 * The result: the deep-link is "lost" on boot and the view opens without its
 * shareable state.
 *
 * Solution: we capture search params from the **first** `location.search`
 * SYNCHRONOUSLY on import of this module (before the router mounts and performs
 * its flush). Routes use this snapshot to seed their state and, on first
 * mount, re-apply them to the URL (with `replace`) if boot lost them. The snapshot is consumed ONCE per key:
 * after reading it is deleted, so later internal navigations never revive an old
 * value (idempotence).
 */

const initialParams = new URLSearchParams(
  typeof globalThis !== "undefined" && globalThis.location ? globalThis.location.search : "",
);

/** Route (pathname) where the app loaded, to avoid applying the snapshot outside it. */
const initialPath =
  typeof globalThis !== "undefined" && globalThis.location ? globalThis.location.pathname : "";

/**
 * Returns (and CONSUMES) the initial value of a search param from the boot
 * deep-link. Only returns it if the app loaded at `pathPrefix` (e.g.
 * `/purchases`, `/stock`, `/lots`), so each route only restores its own.
 * After reading, deletes it from the snapshot: a second read (or an internal
 * navigation) returns `null`.
 */
export function takeInitialSearchParam(key: string, pathPrefix: string): string | null {
  if (!initialPath.startsWith(pathPrefix)) {
    return null;
  }
  const value = initialParams.get(key);
  if (value === null) {
    return null;
  }
  initialParams.delete(key);
  return value;
}
